import React, { Suspense, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getSessionToken,
  getPasswordChangeToken,
  getProfileWithRole,
  hasUserPermission,
  hasValidProtectedRouteSession,
} from '@/application/helpers/storage';
import { setNotFoundNavigate } from '@/application/helpers/navigationBridge';
import {
  canAccessPath,
  isBusinessUserProfile,
  PermissionAction,
} from '@/application/services/permissionService';
import { MainPage, ReportPage } from './lazyPages';

export const LazyLoad = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={null}>{children}</Suspense>
);

export const ProtectedRoute = ({
  children,
  pageName,
}: {
  children: React.ReactNode;
  pageName?: string;
}) => {
  if (!hasValidProtectedRouteSession()) {
    return <Navigate to="/login" replace />;
  }

  // Set page title if pageName is provided
  if (pageName) {
    document.title = pageName;
  }

  return <>{children}</>;
};

export const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

export const ProtectedLoginRoute = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const sessionToken = getSessionToken();

  if (!sessionToken) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export const ProtectedPasswordChangeRoute = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const passwordChangeToken = getPasswordChangeToken();

  if (!passwordChangeToken) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Permission route that derives required permission key from current path via sidebar config
 * and validates access by explicit action (default: read).
 */
export const PathBasedPermissionRoute = ({
  children,
  fallbackPath = '/',
  action,
}: {
  children: React.ReactNode;
  fallbackPath?: string;
  action?: PermissionAction;
}) => {
  const location = useLocation();
  const userProfile = getProfileWithRole();
  const hasAccess = canAccessPath({
    pathname: location.pathname,
    userProfile,
    hasPermission: hasUserPermission,
    action: action ?? 'read',
    // Fail-closed for protected routes when no permission key is mapped.
    fallbackIfNoPermissionKey: false,
  });
  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }
  return <>{children}</>;
};

/**
 * Composed helper to reduce repetitive wrapper stacks:
 * PathBasedPermissionRoute -> ProtectedRoute -> LazyLoad.
 */
export const GuardedPageRoute = ({
  children,
  pageName,
  fallbackPath = '/',
  action,
}: {
  children: React.ReactNode;
  pageName: string;
  fallbackPath?: string;
  action?: PermissionAction;
}) => (
  <PathBasedPermissionRoute fallbackPath={fallbackPath} action={action}>
    <ProtectedRoute pageName={pageName}>
      <LazyLoad>{children}</LazyLoad>
    </ProtectedRoute>
  </PathBasedPermissionRoute>
);

/**
 * Staff-only guarded route for report sub-pages.
 */
export const StaffGuardedReportPageRoute = ({
  children,
  pageName,
  fallbackPath = '/',
  action,
}: {
  children: React.ReactNode;
  pageName: string;
  fallbackPath?: string;
  action?: PermissionAction;
}) => (
  <PathBasedPermissionRoute fallbackPath={fallbackPath} action={action}>
    <StaffReportsSubPageOnlyRoute>
      <ProtectedRoute pageName={pageName}>
        <LazyLoad>{children}</LazyLoad>
      </ProtectedRoute>
    </StaffReportsSubPageOnlyRoute>
  </PathBasedPermissionRoute>
);

/**
 * For path "/": show Dashboard if user has dashboard.read (staff) or is Business user;
 * otherwise show HomePage.
 * Note: dashboard.read is in ALWAYS_ALLOWED_PERMISSIONS, so staff always see Dashboard.
 * Business users always see Dashboard (no permission check).
 */
export const DashboardOrHomeRoute = () => {
  const { t } = useTranslation();
  return (
    <ProtectedRoute pageName={t('SIDEBAR.DASHBOARD')}>
      <LazyLoad>
        <MainPage />
      </LazyLoad>
    </ProtectedRoute>
  );
};

/**
 * Staff: `/reports` should land on `/reports/p2w` by default.
 * Business: keep showing the existing report page on `/reports`.
 */
export const ReportsLandingRoute = () => {
  const userProfile = getProfileWithRole();
  const isBusiness = isBusinessUserProfile(userProfile);

  if (!isBusiness) {
    return <Navigate to="/reports/p2w" replace />;
  }

  return (
    <LazyLoad>
      <ReportPage />
    </LazyLoad>
  );
};

/**
 * Staff-only report sub-pages.
 * - If business user manually visits `/reports/p2w`,
 *   redirect back to `/reports`.
 */
export const NotFoundNavigationBinder = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setNotFoundNavigate((to, opts) => navigate(to, opts));
    return () => setNotFoundNavigate(null);
  }, [navigate]);
  return null;
};

export const StaffReportsSubPageOnlyRoute = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const userProfile = getProfileWithRole();
  const isBusiness = isBusinessUserProfile(userProfile);

  if (isBusiness) {
    return <Navigate to="/reports" replace />;
  }

  return <>{children}</>;
};
